import { useState, useEffect } from 'react'
import { FileText, Save, ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import organizadoresService from '../../services/organizadoresService'

function NumericInput({ value, onChange, placeholder, className }) {
  const [display, setDisplay] = useState(String(value ?? ''))

  useEffect(() => {
    setDisplay(String(value ?? ''))
  }, [value])

  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      placeholder={placeholder}
      className={className}
      onChange={e => {
        const raw = e.target.value.replace(',', '.')
        if (raw === '' || /^[\d]*\.?[\d]*$/.test(raw)) setDisplay(e.target.value)
      }}
      onFocus={e => {
        if (display === '0') { setDisplay(''); e.target.select() }
      }}
      onBlur={() => {
        const num = parseFloat(display.replace(',', '.'))
        const final = isNaN(num) ? 0 : num
        setDisplay(String(final))
        onChange(final)
      }}
    />
  )
}

export default function GtaNotaConfig() {
  const navigate = useNavigate()
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [config, setConfig] = useState({
    gta_valor_por_animal: 0,
    gta_valor_por_gta_nota: 0,
    gta_valor_taxa: 0,
    gta_modo_cobranca: 'por_vendedor',
    gta_cobrar_defesa: false,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    organizadoresService.getConfiguracaoGtaNota(orgId)
      .then(data => setConfig(data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [orgId])

  const handleSave = async () => {
    setSaving(true)
    try {
      await organizadoresService.updateConfiguracaoGtaNota(orgId, config)
      toast.success('Configurações salvas com sucesso')
    } catch (err) {
      toast.error('Erro ao salvar configurações')
    } finally {
      setSaving(false)
    }
  }

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador.
    </div>
  )

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <FileText className="w-5 h-5 text-amber-600" />
        <h1 className="text-lg font-semibold text-gray-800">Taxas GTA / Nota Fiscal</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <p className="text-sm text-gray-600 mb-6">
          Configure os valores padrão que serão usados nos leilões com fluxo GTA/Nota.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor por Animal (R$)
            </label>
            <NumericInput
              value={config.gta_valor_por_animal}
              onChange={v => setConfig(c => ({ ...c, gta_valor_por_animal: v }))}
              placeholder="0,00"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">Cobrado por cabeça de animal</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor por GTA/Nota (R$)
            </label>
            <NumericInput
              value={config.gta_valor_por_gta_nota}
              onChange={v => setConfig(c => ({ ...c, gta_valor_por_gta_nota: v }))}
              placeholder="0,00"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">Valor fixo por GTA/Nota emitida</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Modo de Cobrança
            </label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-lg hover:bg-gray-50 transition-colors">
                <input
                  type="radio" name="gta_modo"
                  value="por_vendedor"
                  checked={config.gta_modo_cobranca === 'por_vendedor'}
                  onChange={e => setConfig({ ...config, gta_modo_cobranca: e.target.value })}
                  className="text-blue-600"
                />
                <span className="text-sm text-gray-700">Por Vendedor</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-lg hover:bg-gray-50 transition-colors">
                <input
                  type="radio" name="gta_modo"
                  value="por_lote"
                  checked={config.gta_modo_cobranca === 'por_lote'}
                  onChange={e => setConfig({ ...config, gta_modo_cobranca: e.target.value })}
                  className="text-blue-600"
                />
                <span className="text-sm text-gray-700">Por Lote</span>
              </label>
            </div>
            <p className="text-xs text-gray-400 mt-1">Como cobrar GTA/Nota no mapa do comprador</p>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Ativar cobranças de GTA e Nota em Situação Defesa?
              </label>
              <p className="text-xs text-gray-400 mt-1">Se ativado, o mapa do vendedor (defesas) mostrará o cálculo de GTA e Nota</p>
            </div>
            <select
              value={config.gta_cobrar_defesa ? 'sim' : 'nao'}
              onChange={e => setConfig({ ...config, gta_cobrar_defesa: e.target.value === 'sim' })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-24"
            >
              <option value="nao">Não</option>
              <option value="sim">Sim</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end mt-8 pt-4 border-t border-gray-100">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>
      </div>
    </div>
  )
}
